import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';

import { ApiService, ConfigService, ProductService } from '@app-core/services';
import { Dialog } from '@app-core/services/dialog';
import { fetchFilters } from '@app-core/utils/fetch-filters';
import { Cart, CartItem, CartTax, LoadQuote, SaveQuote, ValidatedCart } from '@app-model/cart';
import { Markup, UserResponse } from '@app-model/login';
import { GetProductsRequest } from '@app-model/request';
import { GetPostalCodeResponse, GetProductsResponse } from '@app-model/response';
import { QuoteSavedPopupComponent } from '@app-shared/components';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import * as CartActions from './cart.actions';
import * as CartReducer from '../cart/cart.reducer';
import * as ConfigReducer from '../config/config.reducer';
import * as ProductActions from '../product/product.actions';
import * as ProductReducer from '../product/product.reducer';
import * as fromRoot from '../reducers';
import { UpdatePendingNumberAction } from '../user/user.actions';
import * as VehicleActions from '../vehicle/vehicle.actions';

@Injectable()
export class CartEffects {
  private actions$ = inject(Actions);
  private apiService = inject(ApiService);
  private router = inject(Router);
  private store = inject<Store<fromRoot.State>>(Store);
  private _dialog = inject(Dialog);
  private _configService = inject(ConfigService);


  addCart$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.ADD_CART),
      withLatestFrom(
        this.store.select((state) => state.productState.request),
        this.store.select((state) => state.cartState),
        this.store.select((state) => state.userState.user),
        this.store.select((state) => state.configState)
      ),
      mergeMap(
        ([action, productRequest, cartState, user]: [
          CartActions.AddCartAction,
          GetProductsRequest,
          CartReducer.State,
          UserResponse,
          ConfigReducer.State,
        ]) => {
          const cart = [...cartState.validatedCart, JSON.parse(JSON.stringify(action.payload))];
          const request = new Cart(cart, productRequest, user);

          return this.apiService.validateCart(request).pipe(
            // If successful, dispatch success action with result
            map((validatedCart: ValidatedCart) => ({
              type: CartActions.VALIDATE_CART_SUCCESS,
              payload: { validatedCart },
            })),
            // If request fails, dispatch failed action
            catchError((payload) => of({ type: CartActions.CART_FAILURE, payload }))
          );
        }
      )
    )
  );

  deleteCart$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.DELETE_CART),
      withLatestFrom(
        this.store.select((state) => state.productState.request),
        this.store.select((state) => state.cartState),
        this.store.select((state) => state.userState.user)
      ),
      mergeMap(
        ([action, productRequest, cartState, user]: [
          CartActions.AddCartAction,
          GetProductsRequest,
          CartReducer.State,
          UserResponse,
        ]) => {
          const cart = cartState.validatedCart.filter((item) => item.key !== action.payload.key);
          const request = new Cart(cart, productRequest, user);

          return this.apiService.validateCart(request).pipe(
            // If successful, dispatch success action with result
            map((response) => ({ type: CartActions.VALIDATE_CART_SUCCESS, payload: { validatedCart: response } })),
            // If request fails, dispatch failed action
            catchError((payload) => of({ type: CartActions.CART_FAILURE, payload }))
          );
        }
      )
    )
  );

  getCheckoutForm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.GET_CHECKOUT_FORM),
      withLatestFrom(
        this.store.select((state) => state.productState.request),
        this.store.select((state) => state.cartState.cart),
        this.store.select((state) => state.userState.user)
      ),
      switchMap(
        ([, request, cart, user]: [CartActions.GetCheckouFormAction, GetProductsRequest, CartItem[], UserResponse]) => {
          const req = new Cart(cart, request, user);
          const r = { ...req, application: this._configService.appIdentifier, programId: user.programId };

          return this.apiService.getCheckoutForm(r).pipe(
            // If successful, dispatch success action with result
            map((payload) => ({ type: CartActions.GET_CHECKOUT_FORM_SUCCESS, payload })),
            // If request fails, dispatch failed action
            catchError((payload) => of({ type: CartActions.CART_FAILURE, payload }))
          );
        }
      )
    )
  );

  LogOut$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.GET_CHECKOUT_FORM_SUCCESS),
        tap(() => this.router.navigate(['/home/shopping/checkout']))
      ),
    { dispatch: false }
  );

  addTaxes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.ADD_TAXES),
      withLatestFrom(
        this.store.select((state) => state.cartState.validatedCart),
        this.store.select((state) => state.userState.user),
        this.store.select((state) => state.productState.request)
      ),
      switchMap(
        ([, validatedCart, user, productRequest]: [
          CartActions.AddTaxesAction,
          CartItem[],
          UserResponse,
          GetProductsRequest,
        ]) => {
          const request = new CartTax(user, validatedCart, productRequest);

          return this.apiService.taxCalculation(request).pipe(
            // If successful, dispatch success action with result
            map((response: { cart: CartItem[] }) => ({ type: CartActions.ADD_TAXES_SUCCESS, payload: response.cart })),
            // If request fails, dispatch failed action
            catchError((payload) => of({ type: CartActions.CART_FAILURE, payload }))
          );
        }
      )
    )
  );

  submitCart$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SUBMIT_CART),
      withLatestFrom(
        this.store.select((state) => state.userState.user),
        this.store.select((state) => state.vehicleState.values)
      ),
      switchMap(([action, user, productRequest]: [CartActions.SubmitCartAction, UserResponse, {}]) => {
        action.payload.dealer = user.cmsDealerNumber;
        action.payload.request = productRequest;
        if (this._configService.environment.features.supportCustomerTaxExempt) {
          action.payload.request.customerTaxExempt = productRequest['customerTaxExempt'] === 'YES';
        }
        action.payload.programId = user.programId;

        // action.payload.applicationName = user.applicationName;
        // action.payload.userName = user.userName;
        return this.apiService.submitCart(action.payload).pipe(
          // If successful, dispatch success action with result
          withLatestFrom(this.store.select((state) => state.remitState.pendingContracts)),
          map(([response, pendingContracts]) => {
            this.store.dispatch(new UpdatePendingNumberAction(pendingContracts.length + response.submittedCart.length));

            const { invalidCart, submittedCart } = response;

            return { type: CartActions.SUBMIT_CART_SUCCESS, payload: { invalidCart, submittedCart } };
          }),
          // If request fails, dispatch failed action
          catchError((payload) => of({ type: CartActions.CART_FAILURE, payload }))
        );
      })
    )
  );

  saveQuote$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SAVE_AS_QUOTE),
      withLatestFrom(this.store.select((state) => state)),
      switchMap(([, state]: [CartActions.SaveAsQuoteAction, fromRoot.State]) => {
        const quote = new SaveQuote(state.productState.request, state.vehicleState, state.cartState, state.userState);

        if (state.userState.user.isRelatedDealer) {
          quote['productState'] = state.productState;
        }

        return this.apiService.saveQuote(quote).pipe(
          // If successful, dispatch success action with result
          map(() => ({ type: CartActions.SAVE_AS_QUOTE_SUCCESS, payload: {} })),
          tap(() => this._dialog.open(QuoteSavedPopupComponent)),
          // If request fails, dispatch failed action
          catchError(() => of({ type: CartActions.SAVE_AS_QUOTE_FAILURE, payload: {} }))
        );
      })
    )
  );

  SubmitCartSuccess$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.SUBMIT_CART_SUCCESS),
        tap((action: CartActions.SubmitCartSuccessAction) => {
          if (action.payload.invalidCart.length === 0) {
            this.store.dispatch(new VehicleActions.ResetProductFormAction());
            this.store.dispatch(new ProductActions.ResetProductsAction());
          }
        })
      ),
    { dispatch: false }
  );

  loadQuote$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.LOAD_QUOTE),
      withLatestFrom(this.store.select((state) => state)),
      switchMap(([, state]: [CartActions.LoadQuoteAction, fromRoot.State]) => {
        return this.callLoadQuote(state).pipe(
          // If successful, dispatch success action with result
          map((payload: LoadQuote) => ({ type: CartActions.LOAD_QUOTE_SUCCESS, payload })),
          // If request fails, dispatch failed action
          catchError(() => of({ type: CartActions.CART_FAILURE, payload: {} }))
        );
      })
    )
  );

  getCustomerPostal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.GET_CUSTOMER_POSTAL),
      switchMap((action: CartActions.GetCustomerPostalAction) => {
        return this.apiService.getPostal({ postalcode: action.payload }).pipe(
          // If successful, dispatch success action with result
          map((payload: GetPostalCodeResponse) => ({ type: CartActions.GET_CUSTOMER_POSTAL_SUCCESS, payload })),
          // If request fails, dispatch failed action
          catchError(() => of({ type: CartActions.CART_FAILURE, payload: {} }))
        );
      })
    )
  );

  getCosignerPostal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.GET_COSIGNER_POSTAL),
      switchMap((action: CartActions.GetCosignerPostalAction) => {
        return this.apiService.getPostal({ postalcode: action.payload }).pipe(
          // If successful, dispatch success action with result
          map((payload: GetPostalCodeResponse) => ({ type: CartActions.GET_COSIGNER_POSTAL_SUCCESS, payload })),
          // If request fails, dispatch failed action
          catchError(() => of({ type: CartActions.CART_FAILURE, payload: {} }))
        );
      })
    )
  );

  LoadQuoteSuccess$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.LOAD_QUOTE_SUCCESS),
        withLatestFrom(this.store.select((state) => state.userState.markup)),
        tap(([action, markup]: [CartActions.LoadQuoteSuccessAction, Markup]) => {
          const productQuote = this.mapProductsFromLoadQuote(action.payload, markup);

          // change cartFound value to false as this quote was loaded and not decoded manually
          action.payload.VehicleState.vin.cartFound = false;

          this.store.dispatch(new VehicleActions.LoadVehicleQuoteAction(action.payload));
          this.store.dispatch(new ProductActions.LoadProductQuoteAction(productQuote));

          // navigate to product page
          void this.router.navigate(['/home/shopping/products']);
        })
      ),
    { dispatch: false }
  );

  private callLoadQuote(state: fromRoot.State): Observable<LoadQuote> {
    const dealer = state.userState.user.cmsDealerNumber;
    const vin = state.vehicleState.vin.vin;

    const user = state.userState.user;
    if (user && user.isRelatedDealer) {
      return this.apiService.loadQuoteOnly({ dealer, vin });
    }

    return this.apiService.loadQuote({ dealer, vin });
  }

  // this is mapper for loadquote api call
  private mapProductsFromLoadQuote(loadQuoteResponse: LoadQuote, markup: Markup) {
    const productState = loadQuoteResponse['productState'] as ProductReducer.State;

    // if there is a product state, then use that (this happens for PRIME flow)
    if (productState) {
      return {
        request: productState.request,
        response: productState.response,
        gridProducts: productState.visibleProducts,
        filters: productState.filters,
        markup: productState.markup,
      };
    }

    // else recreate the product state from available options and standard response
    const getProductsResponse = new GetProductsResponse();
    getProductsResponse.availableOptions = loadQuoteResponse.availableOptions;
    getProductsResponse.standardResponse = loadQuoteResponse.StandardResponse;
    const filters = fetchFilters(getProductsResponse.availableOptions.products.productItem);

    const rawProducts = loadQuoteResponse.availableOptions.products.productItem;
    const gridProducts = ProductService.productResponseDataMapper(rawProducts, markup);

    return {
      request: loadQuoteResponse.getProductRequest,
      response: getProductsResponse,
      gridProducts: gridProducts,
      filters: filters,
      markup: markup,
    };
  }
}
